"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Menu, X, ArrowRight } from "lucide-react";
import { StatusBadge } from "./technical-components";

interface LandingNavProps {
  onOpenDemo: () => void;
  onOpenVideo: () => void;
}

export function LandingNav({ onOpenDemo, onOpenVideo }: LandingNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { num: "01", name: "Architecture", href: "#dual-engine" },
    { num: "02", name: "Athletic Science", href: "#science" },
    { num: "03", name: "Modules", href: "#modules" },
    { num: "04", name: "Interfaces", href: "#gallery" },
    { num: "05", name: "Hardware IoT", href: "#hardware-iot" },
    { num: "06", name: "FAQ", href: "#faq" },
    { num: "07", name: "Contact", href: "#contact" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 border-b border-[#3A3A38]/20 bg-[#F7F7F5]/90 backdrop-blur-md ${
          scrolled ? "py-3 border-[#3A3A38]/40" : "py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Left: Prominent Logo & Brand Identity */}
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-none border-2 border-[#1A3C2B] flex items-center justify-center overflow-hidden shrink-0 shadow-md">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/media/olympio_logo.jpeg.png"
                alt="Olympio Logo"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-space font-bold text-2xl sm:text-3xl text-[#1A3C2B] tracking-tight uppercase">
                OLYMPIO
              </span>
              <span className="font-mono-tech text-[9px] sm:text-[10px] text-[#3A3A38]/80 uppercase tracking-widest -mt-1 font-bold">
                SYSTEMS INC.
              </span>
            </div>
          </Link>

          {/* Center: Nav links in JetBrains Mono, 10px, uppercase, prefixed with numerical indices */}
          <nav className="hidden md:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => {
                  if (link.action) {
                    e.preventDefault();
                    link.action();
                  }
                }}
                className="font-mono-tech text-[10px] text-[#3A3A38] uppercase tracking-[0.1em] hover:text-[#1A3C2B] transition-colors flex items-center gap-1 group py-1"
              >
                <span className="text-[#1A3C2B] font-bold opacity-60 group-hover:opacity-100">
                  {link.num}.
                </span>
                <span>{link.name}</span>
              </a>
            ))}
          </nav>

          {/* Right side: Ghost button (1px border) and a solid Forest green button */}
          <div className="hidden sm:flex items-center gap-3">
            <Link
              href="/login"
              className="font-mono-tech text-[11px] uppercase tracking-wider px-3.5 py-1.5 border border-[#3A3A38]/30 bg-transparent text-[#1A3C2B] hover:bg-[#1A3C2B]/5 transition rounded-none"
            >
              Sign In
            </Link>

            <button
              onClick={onOpenDemo}
              className="font-mono-tech text-[11px] uppercase tracking-wider px-4 py-1.5 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition border border-[#1A3C2B] rounded-none flex items-center gap-2"
            >
              <span>Book Demo</span>
              <ArrowRight className="w-3 h-3 text-[#9EFFBF]" />
            </button>
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#1A3C2B] border border-[#3A3A38]/30 bg-[#F7F7F5] rounded-none"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-x-0 top-[60px] z-40 bg-[#F7F7F5] border-b border-[#3A3A38]/30 p-6 md:hidden space-y-4 font-mono-tech">
          <div className="space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => {
                  setMobileMenuOpen(false);
                  if (link.action) {
                    e.preventDefault();
                    link.action();
                  }
                }}
                className="block text-xs uppercase tracking-widest text-[#3A3A38] hover:text-[#1A3C2B] py-2 border-b border-[#3A3A38]/10"
              >
                <span className="text-[#1A3C2B] font-bold mr-2">{link.num}.</span>
                {link.name}
              </a>
            ))}
          </div>

          <div className="pt-2 flex flex-col gap-2">
            <Link
              href="/login"
              className="w-full text-center py-2 border border-[#3A3A38]/30 text-xs uppercase tracking-wider text-[#1A3C2B]"
            >
              Sign In
            </Link>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenDemo();
              }}
              className="w-full text-center py-2 bg-[#1A3C2B] text-white text-xs uppercase tracking-wider"
            >
              Book Demo
            </button>
          </div>
        </div>
      )}
    </>
  );
}
